import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './postScore.module.scss'
import ModuleWrapper from 'components/generic/containers/ModuleWrapper'
import { RouterBtn, RouterLink } from 'components/generic/router/links'
import { PageUrls as ProfileUrls, SubPaths } from 'components/profile/ProfileRouter/ProfileRouter'
import Separator from 'components/generic/Separator'
import withInfoModal from 'components/generic/hoc/withInfoModal'
import { Formik, Form } from 'formik'
import { MatchUpTypeEnum, SideEnum } from 'types/api/enums'
import DatePickerWTN from 'components/generic/DatePicker/DatePickerWTN'
import FormInputGroup from 'components/generic/FormInputGroup'
import RadioFieldWTN from 'components/generic/RadioFieldWTN'
import { ReactComponent as SinglesIcon } from 'images/icons/icn_singles.svg'
import { ReactComponent as DoublesIcon } from 'images/icons/icn_doubles.svg'
import SelectWTN from 'components/generic/SelectWTN'
import {
  MatchFormatManagers,
  POST_SCORE_ALLOWED_DATE_LIMIT,
  socialMatchTypeHelperText,
  useSocialMatchFormatOptions,
} from 'forms/post-score'
import { SocialMatchFormat } from 'types/forms/post-score'
import InfoBox from 'components/generic/InfoBox'
import MatchDetails from './MatchDetails'
import BtnPrimary from 'components/generic/BtnPrimary'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { getOwnProfile } from 'actions/profile-actions'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import { MessageTypes } from 'constants/message-screens'
import { ReactComponent as RefreshIcon } from 'images/icons/icn_refresh.svg'
import { submitSocialMatchUp } from 'actions/social-matches-actions'
import { PlayerInput, SelfSubmittedMatchUpInput } from 'types/api/types'
import { FormState as PostScoreFormState } from 'forms/post-score/types'
import { ConstantQueryParamValues, PersonIDEnum, QueryParamNames } from 'types/global'
import { Redirect, useLocation } from 'react-router'
import { BackArrow } from 'components/Icons'
import { createQueryString, createRelPath } from 'constants/global'
import withOnlyAllowLoggedIn from 'components/generic/hoc/withOnlyAllowLoggedIn'
import FeatureNotAvailablePanel from 'components/generic/FeatureNotAvailablePanel'
import { ReactComponent as IconScores } from 'images/icons/icn_large_score.svg'
import { generateDateAtMidnight } from 'utils'
import ErrorScreen, { ErrorScreenProps } from 'components/generic/ErrorScreen'
import { selectOwnProfile, selectOwnProfileError, selectOwnProfileLoading } from 'redux/selectors'
import { LinkProps } from 'react-router-dom'

export const goToMyScoresLink = createRelPath('PLAYER_PROFILE', {
  queryParams: {
    [QueryParamNames.PROFILE_OWN_PROFILE_FLAG]: 'true',
    [QueryParamNames.PROFILE_SECTION]: ConstantQueryParamValues.PROFILE_RESULTS_SECTION,
    [QueryParamNames.PROFILE_RESULTS_SUB_SECTION]:
      ConstantQueryParamValues.PROFILE_SOCIAL_MATCHES_RESULTS_SUB_SECTION,
  },
})

function canSubmit(values) {
  const areSidesSet =
    values.type === MatchUpTypeEnum.SINGLES
      ? !!(values.sides.SIDE1.player1 && values.sides.SIDE2.player1)
      : !!(
          values.sides.SIDE1.player1 &&
          values.sides.SIDE2.player1 &&
          values.sides.SIDE1.player2 &&
          values.sides.SIDE2.player2
        )

  return values.score.matchWinner && values.type && values.date && areSidesSet && values.format
}

function transformMatchDetails(values: PostScoreFormState): SelfSubmittedMatchUpInput {
  const {
    date,
    format,
    sides,
    score: { sets, matchWinner },
  } = values || {}

  const winner = matchWinner as SideEnum
  const loser = winner === SideEnum.SIDE1 ? SideEnum.SIDE2 : SideEnum.SIDE1

  const setInputs: SelfSubmittedMatchUpInput['score']['sets'] = []
  let superTiebreakInput: SelfSubmittedMatchUpInput['score']['superTiebreak'] | undefined

  function getPlayerInputs(sides: PostScoreFormState['sides'], side: SideEnum) {
    return [sides[side].player1, sides[side].player2]
      .map(player =>
        player
          ? {
              personId: {
                identifier: player.tennisID,
                type: PersonIDEnum.TennisID,
              },
            }
          : null
      )
      .filter((player): boolean => {
        return player !== null
      }) as PlayerInput[]
  }

  sets.forEach(set => {
    if (set.isEditable) {
      switch (set.type) {
        case 'set':
          setInputs.push({
            winnerGamesWon: set.points[winner] as number,
            loserGamesWon: set.points[loser] as number,
            tiebreaker: set.tiebreak
              ? {
                  winnerPointsWon: set.tiebreak.points[winner] as number,
                  loserPointsWon: set.tiebreak.points[loser] as number,
                }
              : undefined,
          })
          break
        case 'tiebreak':
          setInputs.push({
            tiebreaker: {
              winnerPointsWon: set.points[winner] as number,
              loserPointsWon: set.points[loser] as number,
            },
          })
          break
        case 'superTiebreak':
          superTiebreakInput = {
            winnerPointsWon: set.points[winner] as number,
            loserPointsWon: set.points[loser] as number,
          }
          break
        default:
          break
      }
    }
  })

  return {
    score: {
      sets: setInputs,
      superTiebreak: superTiebreakInput,
    },
    sides: [
      {
        sideNumber: SideEnum.SIDE1,
        players: getPlayerInputs(sides, SideEnum.SIDE1),
      },
      {
        sideNumber: SideEnum.SIDE2,
        players: getPlayerInputs(sides, SideEnum.SIDE2),
      },
    ],
    winningSide: winner,
    start: date.toISOString(),
    matchUpFormat: format,
  }
}

const HeaderWithModal = withInfoModal('post-score-info-modal', () => {
  const { t: tSocialMatches } = useTranslation('strSocialMatches')
  return <h1 className={styles.title}>{tSocialMatches('postScore.title')}</h1>
})

const ErrorMessageScreen = (props: Pick<ErrorScreenProps, 'type' | 'message'>) => {
  const { t: tMessageScreen } = useTranslation('strMessageScreen')

  const refreshBtn = (
    <button onClick={() => window.location.reload()} className={styles.refreshBtn}>
      <RefreshIcon />
      {tMessageScreen('ownProfileErrorLink')}
    </button>
  )
  return (
    <ModuleWrapper>
      <ErrorScreen {...props} backLink={refreshBtn} />
    </ModuleWrapper>
  )
}

const PostScore = () => {
  const { t: tSocialMatches } = useTranslation('strSocialMatches')
  const { t: tCommonUI } = useTranslation('strCommonUI')
  const matchFormatOptions = useSocialMatchFormatOptions()

  const dispatch = useDispatch()
  const { state } = useLocation<{ fromMyScores?: boolean }>()
  const ownProfile = useSelector(selectOwnProfile)
  const errorOwnProfile = useSelector(selectOwnProfileError)
  const loadingOwnProfile = useSelector(selectOwnProfileLoading)
  const submissionError = useSelector((state: AppState) => state.socialMatches.errorSubmittingMatch)
  const isSubmitting = useSelector((state: AppState) => state.socialMatches.isMatchSubmitting)
  const lastSubmitted = useSelector((state: AppState) => state.socialMatches.lastSubmitted)
  const backLink = useRef<LinkProps['to']>({
    pathname: ProfileUrls.Profile,
    search: createQueryString({
      queryParams: {
        [QueryParamNames.PROFILE_OWN_PROFILE_FLAG]: 'true',
        [QueryParamNames.PROFILE_SECTION]: ConstantQueryParamValues.PROFILE_RESULTS_SECTION,
        [QueryParamNames.PROFILE_RESULTS_SUB_SECTION]:
          ConstantQueryParamValues.PROFILE_SOCIAL_MATCHES_RESULTS_SUB_SECTION,
      },
    }),
  })
  const savedForm = useRef<PostScoreFormState | null>(null)

  useEffect(() => {
    if (!ownProfile && !loadingOwnProfile) dispatch(getOwnProfile())
  }, [dispatch, ownProfile, loadingOwnProfile])

  if (errorOwnProfile)
    return (
      <ErrorMessageScreen type={MessageTypes.OWN_PROFILE_ERROR} message={errorOwnProfile.message} />
    )

  if (!ownProfile || isSubmitting) {
    return (
      <ModuleWrapper>
        <LoadingSpinnerWithContainer containerHeight={600} />
      </ModuleWrapper>
    )
  }

  if (lastSubmitted) {
    return (
      <Redirect
        push
        to={{ pathname: ProfileUrls.Profile, hash: SubPaths.SubmissionSuccess, state }}
      />
    )
  }

  return (
    <ModuleWrapper>
      {state?.fromMyScores ? (
        <RouterLink type='backLink' to={backLink.current} className={styles.backLink}>
          {tCommonUI('link.back')}
        </RouterLink>
      ) : (
        <BtnPrimary border={false} href={goToMyScoresLink} className={styles.backLink}>
          <BackArrow aria-hidden />
          {tCommonUI('link.back')}
        </BtnPrimary>
      )}

      {!ownProfile?.eligibleForSelfSubmission ? (
        <FeatureNotAvailablePanel icon={<IconScores />} />
      ) : (
        <article>
          <HeaderWithModal
            widgetElId='player-profile'
            modalHeaderText={tSocialMatches('postScore.modals.info.title')}
            modalAcceptBtnText={tCommonUI('button.okGotIt')}
            wrapperClass={styles.titleWrapper}
            triggerClass={styles.infoModalTrigger}
          >
            <p>{tSocialMatches('postScore.modals.info.info.1')}</p>
            <p>{tSocialMatches('postScore.modals.info.info.2')}</p>
            <p className={styles.mb0}>{tSocialMatches('postScore.modals.info.info.3')}</p>
          </HeaderWithModal>

          <p>{tSocialMatches('postScore.subtitle')}</p>

          <Separator />

          <Formik
            enableReinitialize
            initialValues={
              submissionError && savedForm.current
                ? savedForm.current
                : {
                    date: generateDateAtMidnight(),
                    type: null,
                    format: SocialMatchFormat.THREE_SETS_SIX_GAMES,
                    sides: {
                      [SideEnum.SIDE1]: {
                        player1: ownProfile,
                        player2: null,
                      },
                      [SideEnum.SIDE2]: {
                        player1: null,
                        player2: null,
                      },
                    },
                    score:
                      MatchFormatManagers[
                        SocialMatchFormat.THREE_SETS_SIX_GAMES
                      ].createInitialScore(),
                    scoreErrors: [],
                  }
            }
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async values => {
              if (canSubmit(values)) {
                const matchInput: SelfSubmittedMatchUpInput = transformMatchDetails(values)

                dispatch(submitSocialMatchUp(matchInput))
                savedForm.current = values
              }
            }}
          >
            {({ errors, values, validateField, setTouched, setFieldValue }) => {
              const today = new Date()
              const oldestAllowedDate = new Date(today)
              oldestAllowedDate.setDate(oldestAllowedDate.getDate() - POST_SCORE_ALLOWED_DATE_LIMIT)

              return (
                <Form>
                  <label htmlFor='matchDate' className={styles.sectionTitle}>
                    {tSocialMatches('postScore.matchDate.title')}
                  </label>
                  <DatePickerWTN
                    id='matchDate'
                    selected={values.date}
                    setSelected={date => {
                      setFieldValue('date', date)
                    }}
                    maxDate={today}
                    minDate={oldestAllowedDate}
                    classInput={styles.matchDatePicker}
                    hideButtons
                  />

                  <Separator />

                  <FormInputGroup
                    label={tSocialMatches('postScore.matchType.title')}
                    formikName={'type'}
                    classLabel={styles.sectionTitle}
                  >
                    <div role='group' className={styles.matchTypeBtnWrapper}>
                      <RadioFieldWTN
                        name={'type'}
                        value={MatchUpTypeEnum.SINGLES}
                        text={tCommonUI('button.singles')}
                        type='button'
                        icon={<SinglesIcon />}
                        className={styles.radioSingles}
                      />
                      <RadioFieldWTN
                        name={'type'}
                        value={MatchUpTypeEnum.DOUBLES}
                        text={tCommonUI('button.doubles')}
                        type='button'
                        icon={<DoublesIcon />}
                        className={styles.radioDoubles}
                      />
                    </div>
                  </FormInputGroup>

                  <Separator />

                  {values.type && (
                    <>
                      <FormInputGroup
                        label={tSocialMatches('postScore.matchFormat.title')}
                        formikName={'format'}
                        classLabel={styles.sectionTitle}
                      >
                        <SelectWTN
                          options={matchFormatOptions}
                          className={styles.matchFormatSelect}
                          name={'format'}
                          isSearchable={false}
                          onChangeCallback={val => {
                            setFieldValue('score', MatchFormatManagers[val].createInitialScore())
                          }}
                          aria-label='Match format'
                        />
                      </FormInputGroup>
                      <InfoBox
                        boxColor='transparent'
                        hasShadow={false}
                        className={styles.matchFormatInfoBox}
                        classIcon={styles.matchFormatInfoBoxIcon}
                        classContent={styles.matchFormatInfoBoxContent}
                      >
                        <p className={styles.matchFormatInfoBoxTitle}>
                          {tSocialMatches('postScore.matchFormat.infoBoxTitle')}
                        </p>
                        {socialMatchTypeHelperText[values.format]()}
                      </InfoBox>

                      <Separator />

                      <label htmlFor='matchScore' className={styles.sectionTitle}>
                        {tSocialMatches('postScore.matchScore.title')}
                      </label>
                      <MatchDetails />

                      <Separator />
                    </>
                  )}
                  <div className={styles.btnsContainer}>
                    <RouterBtn to={backLink.current} type='btnSecondary' size='medium'>
                      {tCommonUI('button.cancel')}
                    </RouterBtn>
                    <BtnPrimary
                      type='submit'
                      size='medium'
                      onClick={() => {
                        window.scrollTo(0, 0)
                      }}
                      isDisabled={!canSubmit(values)}
                    >
                      {tCommonUI('button.submit')}
                    </BtnPrimary>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </article>
      )}
    </ModuleWrapper>
  )
}

export default withOnlyAllowLoggedIn(PostScore)
